import React, {useState} from 'react'
import {useMutation} from 'react-apollo'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import {AlertDialog} from '../../../Common/Dialog'
import messages from '../../../../assets/messages'
import {CONFIRM_PARTNER_LEGAL_ACCEPTANCE_MUTATION} from '../../../../graphql/mutations'
import {CLIENT_DATA_QUERY} from '../../../../graphql/queries'
import {config} from '../../../../config'

interface OwnProps {
  open: boolean
  // Called when the client refuses to accept; the parent handler logs them
  // out so the banner reappears on next sign-in (no implicit consent).
  onCancel: () => void
}

// Inner component receives `t` (and the rest of the WithNamespaces shape)
// from the HOC. `withNamespaces()` requires the wrapped component's prop
// type to extend WithNamespaces — that's why we model OwnProps & WithNamespaces.
type Props = OwnProps & WithNamespaces

/**
 * Renders either a clickable link (when a per-brand URL is configured) or
 * just bold text. Falling back to bold keeps the banner functional in
 * environments where legal links haven't been wired into config.legalLinks
 * yet — partner can still confirm; we just can't deep-link to the docs.
 */
const renderLink = (label: string, href: string | undefined) => {
  if (!href) {
    return <strong>{label}</strong>
  }
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </Link>
  )
}

/**
 * Solicitation Acceptance banner — PT-5333.
 *
 * Shown on every login (and re-shown after refresh) until the client confirms
 * by calling the `confirmPartnerLegalAcceptance` mutation, which clears the
 * `requiresLegalConfirmation` flag on `members.metadata`. Eagle API sets that
 * flag during partner-API registration (IB 357655); see
 * apps/eagle-api/src/services/registerClient.service.ts.
 *
 * The Confirm action triggers a refetch of CLIENT_DATA_QUERY so the rest of
 * the app immediately sees the cleared flag (no stale-cache flicker). Cancel
 * delegates to the parent's logout handler — banner reappears next session.
 *
 * Translations: dashboard pins react-i18next v8, which only exposes the HOC
 * API (no `useTranslation` hook) — we get `t` via withNamespaces() at the
 * bottom of the file, matching the rest of the codebase.
 */
const SolicitationAcceptanceBannerComponent: React.FC<Props> = ({open, onCancel, t}) => {
  const [submitting, setSubmitting] = useState(false)
  const [confirmLegal, {error}] = useMutation(CONFIRM_PARTNER_LEGAL_ACCEPTANCE_MUTATION, {
    // Refetching CLIENT_DATA_QUERY (and awaiting it) ensures every consumer
    // re-renders with the cleared flag before this dialog unmounts.
    refetchQueries: [{query: CLIENT_DATA_QUERY}],
    awaitRefetchQueries: true,
  })

  const links = (config.legalLinks || {}) as {
    terms?: string
    legal?: string
    privacy?: string
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await confirmLegal()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AlertDialog
      open={open}
      title={t(messages.solicitationAcceptanceTitle.i18nKey, messages.solicitationAcceptanceTitle.defaults)}
      onAgree={handleConfirm}
      onDisagree={onCancel}
      onClose={onCancel}
      agreeText={t(messages.confirm.i18nKey, messages.confirm.defaults)}
      disagreeText={t(messages.cancel.i18nKey, messages.cancel.defaults)}
      buttonLoading={submitting}
      errorText={error ? error.message : undefined}
      maxWidth="sm"
    >
      <Typography variant="body1" gutterBottom>
        <Trans {...messages.solicitationAcceptancePart1} />
      </Typography>
      <Typography variant="body1" gutterBottom>
        <Trans {...messages.solicitationAcceptancePart2} />
      </Typography>
      <Typography variant="body1" gutterBottom>
        <Trans {...messages.partnerLegalConsentIntro} />
      </Typography>
      <ul>
        <li>{renderLink(t(messages.partnerLegalConsentTerms.i18nKey, messages.partnerLegalConsentTerms.defaults), links.terms)}</li>
        <li>{renderLink(t(messages.partnerLegalConsentLegal.i18nKey, messages.partnerLegalConsentLegal.defaults), links.legal)}</li>
        <li>{renderLink(t(messages.partnerLegalConsentPrivacy.i18nKey, messages.partnerLegalConsentPrivacy.defaults), links.privacy)}</li>
      </ul>
    </AlertDialog>
  )
}

// Wrap with withNamespaces() so `t` is injected as a prop — keeps the named
// export contract (`{SolicitationAcceptanceBanner}` in src/components/App)
// untouched for callers, who only need to pass OwnProps.
export const SolicitationAcceptanceBanner = withNamespaces()(SolicitationAcceptanceBannerComponent) as unknown as React.FC<OwnProps>
